import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Table, DatePicker, Row, Col, Space, Typography, message, Modal, Upload, Alert, Divider } from 'antd';
import { UploadOutlined, DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import { getPermission } from '../../constants';
import * as XLSX from 'xlsx';

const { Option } = Select;
const { Title } = Typography;
const { Dragger } = Upload;

const userPermission = getPermission(); // Fetch permissions

const typeList = [
    ...(userPermission?.is_positive_adjustment ? [{ id: 'positive', name: 'Positive' }] : []),
    ...(userPermission?.is_negative_adjustment ? [{ id: 'negative', name: 'Negative' }] : []),
    ...(userPermission?.is_wastage ? [{ id: 'wastage', name: 'Wastage' }] : [])
];

const PostAdjustment = () => {
    const [form] = Form.useForm();
    const [customers, setCustomers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [products, setProducts] = useState([{ id: '', quantity: '', type: '', default: true, inventory_id: null }]);
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [total, setTotal] = useState(0);
    const [selectedProductDetails, setSelectedProductDetails] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [currentProductIndex, setCurrentProductIndex] = useState(null);
    
    // Excel import states
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [importedData, setImportedData] = useState([]);
    const [importLoading, setImportLoading] = useState(false);
    const [notFoundProducts, setNotFoundProducts] = useState([]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await axiosInstance.get('/products');
                setProductsList(response.data.products);
            } catch (error) {
                message.error('Failed to fetch products.');
            }
        };
        fetchProducts();
    }, []);

    const handleProductChange = (index, field, value) => {
        setProducts((prevProducts) => {
            const updatedProducts = [...prevProducts];

            if (field === 'id') {
                updatedProducts[index][field] = value;

                const selectedProduct = productsList.find((prod) => prod.id === value);

                if (selectedProduct) {
                    updatedProducts[index]['selling_price'] = selectedProduct.selling_price || 0;
                    updatedProducts[index]['buying_price'] = selectedProduct.buying_price || 0;
                }
            } else {
                updatedProducts[index][field] = value;
            }

            return updatedProducts;
        });
    };

    const addProductRow = () => {
        setProducts([...products, { id: '', quantity: '', type: '', default: true, inventory_id: null }]);
    };

    const removeProductRow = (index) => {
        const updatedProducts = [...products];
        updatedProducts.splice(index, 1);
        setProducts(updatedProducts);
    };

    const filterAccountsByType = (type) => {
        setFilteredAccounts(accounts.filter(account => account.type === type));
    };

    const confirmSubmit = (values) => {
        Modal.confirm({
            title: 'Confirm Sales Submission',
            content: 'Are you sure you want to submit this Stock Adjustment?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => handleSubmit(values),
        });
    };

    const handleSubmit = async (values) => {
        setLoading(true);

        const payload = {
            products: products.map((product) => ({
                id: product.id,
                quantity: product.quantity,
                default: product.default,
                type: product.type,
                inventory_id: product.inventory_id || null
            })),
        };

        try {
            const createResponse = await axiosInstance.post('/adjustment/create', payload);

            if (createResponse.status === 200) {
                message.success('Product Adjusted Successfully!');
                form.resetFields();
                setProducts([{ id: '', quantity: '', type: '', default: true, inventory_id: null }]);
            } else {
                message.error('Failed to Adjust Product. Please try again.');
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.error) {
                message.error(error.response.data.error);
            } else {
                message.error('An unexpected error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const openModal = async (index) => {
        try {
            const selectedProductId = products[index].id;
            const response = await axiosInstance.get(`/sales/sales-prices/${selectedProductId}`);
            if (response.data) {
                setSelectedProductDetails(response.data.details);
                setCurrentProductIndex(index);
                setModalVisible(true);
            }
        } catch (error) {
            console.error('Failed to fetch product details:', error);
        }
    };

    const handleSelectPrice = (record) => {
        setProducts((prevProducts) => {
            return prevProducts.map((product, index) =>
                index === currentProductIndex
                    ? { ...product, default: record.default, inventory_id: record.default ? null : record.id }
                    : product
            );
        });
        setModalVisible(false);
    };

    // Get selected inventory ID for current product
    const getSelectedInventoryId = () => {
        if (currentProductIndex !== null && products[currentProductIndex]) {
            const product = products[currentProductIndex];
            return product.default ? 'default' : product.inventory_id;
        }
        return null;
    };

    // Excel import functions
    const downloadTemplate = () => {
        const templateData = [
            { barcode: 'SAMPLE001', name: 'Sample Product 1', type: 'positive', quantity: 10 },
            { barcode: 'SAMPLE002', name: 'Sample Product 2', type: 'negative', quantity: 5 }
        ];

        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
        XLSX.writeFile(workbook, 'product_import_template.xlsx');
        message.success('Template downloaded successfully!');
    };

    const handleFileUpload = (file) => {
        setImportLoading(true);
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                // Validate and process imported data
                processImportedData(jsonData);
            } catch (error) {
                message.error('Error reading Excel file. Please check the format.');
                setImportLoading(false);
            }
        };

        reader.readAsArrayBuffer(file);
        return false; // Prevent default upload behavior
    };

    const processImportedData = (data) => {
        const processedProducts = [];
        const notFound = [];

        data.forEach((row, index) => {
            const { barcode, name, type, quantity } = row;

            if (!barcode && !name) {
                return; // Skip empty rows
            }

            if (!quantity || quantity <= 0) {
                message.warning(`Row ${index + 1}: Invalid quantity`);
                return;
            }

            if (!typeList.find(t => t.id === type)) {
                message.warning(`Row ${index + 1}: Invalid type "${type}"`);
                return;
            }

            // Search for product by barcode or name
            let foundProduct = null;
            if (barcode) {
                foundProduct = productsList.find(p => p.barcode === barcode);
            }
            if (!foundProduct && name) {
                foundProduct = productsList.find(p => p.name.toLowerCase() === name.toLowerCase());
            }

            if (foundProduct) {
                processedProducts.push({
                    id: foundProduct.id,
                    quantity: parseFloat(quantity),
                    type: type,
                    default: true,
                    inventory_id: null,
                    selling_price: foundProduct.selling_price || 0,
                    buying_price: foundProduct.buying_price || 0
                });
            } else {
                notFound.push({
                    barcode: barcode || '',
                    name: name || '',
                    type,
                    quantity,
                    row: index + 1
                });
            }
        });

        setImportedData(processedProducts);
        setNotFoundProducts(notFound);
        setImportLoading(false);

        if (processedProducts.length > 0) {
            message.success(`${processedProducts.length} products processed successfully`);
        }
        if (notFound.length > 0) {
            message.warning(`${notFound.length} products not found in system`);
        }
    };

    const applyImportedData = () => {
        if (importedData.length === 0) {
            message.warning('No valid products to import');
            return;
        }

        // Remove the default empty row if it exists and is empty
        const filteredProducts = products.filter(p => p.id !== '' || p.quantity !== '' || p.type !== '');
        
        // Combine existing products with imported ones
        const newProducts = [...filteredProducts, ...importedData];
        
        setProducts(newProducts);
        setImportModalVisible(false);
        setImportedData([]);
        setNotFoundProducts([]);
        message.success(`${importedData.length} products added to the list`);
    };

    const productColumns = [
        {
            title: 'Product',
            dataIndex: 'id',
            render: (_, record, index) => (
                <Select
                    showSearch
                    value={record.id}
                    onChange={(value) => handleProductChange(index, 'id', value)}
                    style={{ width: '200px' }}
                    placeholder="Select Product"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        option?.children.toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {productsList.map((prod) => (
                        <Option key={prod.id} value={prod.id}>
                            {prod.name}
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            render: (_, record, index) => (
                <Space style={{ width: '100%' }}>
                    <Select
                        value={record.type}
                        onChange={(value) => handleProductChange(index, 'type', value)}
                        style={{ width: '200px' }}
                        placeholder="Select Type"
                    >
                        <Option key="" value="">
                            Select
                        </Option>
                        {typeList.map((type) => (
                            <Option key={type.id} value={type.id}>
                                {type.name}
                            </Option>
                        ))}
                    </Select>
                    <Button onClick={() => openModal(index)}>Select Inventory</Button>
                </Space>
            ),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.quantity}
                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    min={1}
                    placeholder="Enter Quantity"
                />
            ),
        },
        {
            title: 'Action',
            render: (_, __, index) => (
                index > 0 && (
                    <Button type="link" danger onClick={() => removeProductRow(index)}>
                        Remove
                    </Button>
                )
            ),
        },
    ];

    const importPreviewColumns = [
        { title: 'Product Name', dataIndex: 'name', key: 'name' },
        { title: 'Type', dataIndex: 'type', key: 'type' },
        { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
        { title: 'Status', key: 'status', render: () => <span style={{ color: 'green' }}>✓ Found</span> }
    ];

    const notFoundColumns = [
        { title: 'Row', dataIndex: 'row', key: 'row' },
        { title: 'Barcode', dataIndex: 'barcode', key: 'barcode' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Type', dataIndex: 'type', key: 'type' },
        { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
        { title: 'Status', key: 'status', render: () => <span style={{ color: 'red' }}>✗ Not Found</span> }
    ];

    return (
        <div>
            <Title level={3}>Post Stock Adjustment</Title>
            <Form
                form={form}
                layout="vertical"
                onFinish={confirmSubmit}
            >
                <Space style={{ marginBottom: 16 }}>
                    <Button
                        type="dashed"
                        onClick={addProductRow}
                        style={{ width: '150px' }}
                    >
                        Add Product
                    </Button>
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setImportModalVisible(true)}
                        style={{ width: '150px' }}
                    >
                        Import Excel
                    </Button>
                </Space>

                <Table
                    rowKey={(record, index) => index}
                    columns={productColumns}
                    dataSource={products}
                    pagination={false}
                    size="small"
                    style={{ marginBottom: 20 }}
                    scroll={{ x: 'max-content' }}
                />

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        style={{ margin: '0 auto', width: '200px' }}
                    >
                        Submit Stock Adjustment
                    </Button>
                </Form.Item>
            </Form>

            {/* Product Details Modal */}
            <Modal
                title="Product Details"
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
            >
                <Table
                    dataSource={selectedProductDetails}
                    rowKey="id"
                    columns={[
                        { title: 'Buying Price', dataIndex: 'buying_price' },
                        { title: 'Selling Price', dataIndex: 'selling_price' },
                        { title: 'Quantity', dataIndex: 'quantity' },
                        { title: 'Expire Date', dataIndex: 'expire_date' },
                        {
                            title: 'Action',
                            render: (_, record) => {
                                const selectedId = getSelectedInventoryId();
                                const isSelected = record.default ? selectedId === 'default' : selectedId === record.id;
                                
                                return (
                                    <Button
                                        type={isSelected ? "primary" : "default"}
                                        onClick={() => handleSelectPrice(record)}
                                        style={{
                                            backgroundColor: isSelected ? '#1890ff' : undefined,
                                            borderColor: isSelected ? '#1890ff' : undefined,
                                            color: isSelected ? 'white' : undefined
                                        }}
                                    >
                                        {isSelected ? 'Selected' : 'Select'}
                                    </Button>
                                );
                            },
                        },
                    ]}
                    pagination={false}
                    rowClassName={(record) => {
                        const selectedId = getSelectedInventoryId();
                        const isSelected = record.default ? selectedId === 'default' : selectedId === record.id;
                        return isSelected ? 'selected-inventory-row' : '';
                    }}
                    footer={() => {
                        const totalQuantity = selectedProductDetails.reduce((total, record) => total + record.quantity, 0);
                        return (
                            <div>
                                <strong>Total Quantity: {totalQuantity}</strong>
                            </div>
                        );
                    }}
                />
                <style jsx>{`
                    .selected-inventory-row {
                        background-color: #e6f7ff !important;
                    }
                    .selected-inventory-row:hover {
                        background-color: #bae7ff !important;
                    }
                `}</style>
            </Modal>

            {/* Excel Import Modal */}
            <Modal
                title="Import Products from Excel"
                visible={importModalVisible}
                onCancel={() => {
                    setImportModalVisible(false);
                    setImportedData([]);
                    setNotFoundProducts([]);
                }}
                footer={[
                    <Button key="template" icon={<DownloadOutlined />} onClick={downloadTemplate}>
                        Download Template
                    </Button>,
                    <Button key="cancel" onClick={() => {
                        setImportModalVisible(false);
                        setImportedData([]);
                        setNotFoundProducts([]);
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="apply"
                        type="primary"
                        onClick={applyImportedData}
                        disabled={importedData.length === 0}
                        loading={importLoading}
                    >
                        Apply Import ({importedData.length} products)
                    </Button>,
                ]}
                width={800}
            >
                <Alert
                    message="Important Notice"
                    description="The imported list is not saved automatically. It only fills the items in the product list for this session. You must manually submit the form to save the adjustments."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                <div style={{ marginBottom: 16 }}>
                    <Title level={5}>Upload Excel File</Title>
                    <p>Expected format: barcode, name, type, quantity</p>
                    <p>Supported types: {typeList.map(t => t.name).join(', ')}</p>
                </div>

                <Dragger
                    accept=".xlsx,.xls"
                    beforeUpload={handleFileUpload}
                    showUploadList={false}
                    loading={importLoading}
                >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
                    <p className="ant-upload-hint">
                        Support for .xlsx and .xls files only
                    </p>
                </Dragger>

                {importedData.length > 0 && (
                    <>
                        <Divider>Successfully Processed Products ({importedData.length})</Divider>
                        <Table
                            dataSource={importedData.map((item, index) => ({
                                ...item,
                                key: index,
                                name: productsList.find(p => p.id === item.id)?.name || 'Unknown'
                            }))}
                            columns={importPreviewColumns}
                            pagination={false}
                            size="small"
                            style={{ marginBottom: 16 }}
                        />
                    </>
                )}

                {notFoundProducts.length > 0 && (
                    <>
                        <Divider>Products Not Found ({notFoundProducts.length})</Divider>
                        <Table
                            dataSource={notFoundProducts}
                            columns={notFoundColumns}
                            pagination={false}
                            size="small"
                            rowKey={(record, index) => index}
                        />
                    </>
                )}
            </Modal>
        </div>
    );
};

export default PostAdjustment;